import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ssoService } from "@/services/sso-service";
import { useAdminAuthStore } from "@/stores/admin-auth-store";
import pwcLogoLogin from "@/assets/images/pwc-logo-login.svg";

const SSO_REDIRECT_FLAG = "sso_redirect_flow";

const isMobileDevice = () =>
  typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ssoLogin, isLoading, isAuthenticated } = useAdminAuthStore();

  const isSsoCallback = !!(searchParams.get("code") || searchParams.get("error"));
  const [isRedirectFlow] = useState(
    () => isSsoCallback && !window.opener && sessionStorage.getItem(SSO_REDIRECT_FLAG) === "true",
  );
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [ssoProcessing, setSsoProcessing] = useState(isRedirectFlow);
  const ssoHandledRef = useRef(false);

  useEffect(() => {
    const hasToken = !!localStorage.getItem("admin_access_token");
    if (hasToken || isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      const hasToken = !!localStorage.getItem("admin_access_token");
      if (hasToken) {
        window.location.replace(`${import.meta.env.BASE_URL}dashboard`);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    if (!isSsoCallback) return;
    if (ssoHandledRef.current) return;
    ssoHandledRef.current = true;

    const code = searchParams.get("code");
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    if (isRedirectFlow) {
      sessionStorage.removeItem(SSO_REDIRECT_FLAG);
      if (code) {
        ssoService
          .exchangeToken(code)
          .then((tokens) => {
            if (!tokens) return navigate("/dashboard", { replace: true });
            return ssoLogin(tokens.access_token).then(() =>
              navigate("/dashboard", { replace: true }),
            );
          })
          .catch((err: any) => {
            const msg =
              err.response?.data?.detail ||
              err.response?.data?.error?.message ||
              err.message ||
              "SSO 로그인 처리 실패";
            setSsoError(msg);
            setSsoProcessing(false);
            navigate("/login", { replace: true });
          });
      } else if (error) {
        setSsoError(errorDescription?.replace(/[\r\n]/g, "") || "SSO login failed.");
        setSsoProcessing(false);
        navigate("/login", { replace: true });
      }
      return;
    }

    // Popup flow: relay result to parent window, then close
    const payload = { type: "sso-callback", code, error, error_description: errorDescription };
    if (window.opener) {
      window.opener.postMessage(payload, window.location.origin);
      window.close();
    } else {
      const channel = new BroadcastChannel("sso-login");
      channel.postMessage(payload);
      channel.close();
      window.close();
    }
  }, [isSsoCallback, isRedirectFlow, searchParams, ssoLogin, navigate]);

  const handleSsoLogin = async () => {
    setSsoError(null);
    try {
      const url = await ssoService.getLoginUrl();

      if (isMobileDevice()) {
        sessionStorage.setItem(SSO_REDIRECT_FLAG, "true");
        window.location.assign(url);
        return;
      }

      const popup = window.open(url, "sso-login", "width=500,height=700,left=200,top=100");
      if (!popup) {
        setSsoError("팝업이 차단되었습니다. 팝업 차단을 해제해 주세요.");
        return;
      }

      let handled = false;
      const handleSsoMessage = async (data: any) => {
        if (data?.type !== "sso-callback") return;
        if (handled) return;
        handled = true;
        cleanup();
        try {
          popup.close();
        } catch {
          /* already closed */
        }

        const { code, error, error_description } = data;
        if (code) {
          setSsoProcessing(true);
          try {
            const tokens = await ssoService.exchangeToken(code);
            if (!tokens) {
              navigate("/dashboard", { replace: true });
              return;
            }
            await ssoLogin(tokens.access_token);
            navigate("/dashboard", { replace: true });
          } catch (err: any) {
            const msg =
              err.response?.data?.detail ||
              err.response?.data?.error?.message ||
              err.message ||
              "SSO 로그인 처리 실패";
            setSsoError(msg);
            setSsoProcessing(false);
          }
        } else if (error) {
          setSsoError(
            error === "access_denied"
              ? "접근 권한이 없습니다. 관리자에게 문의해 주세요."
              : error_description?.replace(/[\r\n]/g, "") || "SSO login failed.",
          );
        }
      };

      const messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        handleSsoMessage(event.data);
      };

      const channel = new BroadcastChannel("sso-login");
      channel.onmessage = (event) => handleSsoMessage(event.data);
      window.addEventListener("message", messageListener);

      const cleanup = () => {
        window.removeEventListener("message", messageListener);
        channel.close();
      };

      setTimeout(
        () => {
          if (!handled) cleanup();
        },
        5 * 60 * 1000,
      );
    } catch (err: any) {
      setSsoError(err.message || "SSO 로그인을 사용할 수 없습니다.");
    }
  };

  if (isSsoCallback) {
    return isRedirectFlow ? (
      <div className="flex h-screen w-screen items-center justify-center">
        <p className="text-lg">로그인 처리 중...</p>
      </div>
    ) : null;
  }

  return (
    <div className="min-h-screen bg-white flex justify-between">
      {/* Left Panel */}
      <div className="w-[608px] ml-[100px] flex flex-col gap-[120px] mt-[32px]">
        <img
          src={pwcLogoLogin}
          alt="PwC AI Agent Marketplace"
          className="w-[389px] h-[70px] object-contain"
        />

        <div className="flex flex-col gap-12">
          <div className="flex flex-col gap-4">
            <h1
              className=" text-[56px] font-normal text-[#202020] leading-[64px]"
              style={{ letterSpacing: "-1.68px" }}
            >
              Login
            </h1>
            <p className="text-2xl text-[#000000] leading-8" style={{ letterSpacing: "-0.48px" }}>
              👋 Welcome to Admin Portal
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={handleSsoLogin}
              disabled={ssoProcessing || isLoading}
              className="h-12 w-full flex items-center justify-center bg-[#FFAA72] rounded-[4px] text-base leading-6 font-bold text-black hover:bg-[#FF9955] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              아웃룩으로 로그인
            </button>

            {ssoError && <p className="text-sm text-red-500 text-center">{ssoError}</p>}
          </div>
        </div>
      </div>

      {/* Right Panel - Banner */}
      <div className="flex-1 flex justify-end p-[32px]">
        <div className="h-full">
          <img
            src={`${import.meta.env.BASE_URL}images/login-bgr.png`}
            alt=""
            className="w-full h-full object-cover rounded-[32px]"
          />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
